import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import style from './Item.css'

/**
 * Item is a flexible, composable row used to display a piece of content such as
 * a person, a file or a setting. Combine it with [Item Media](/components/item-media/),
 * [Item Content](/components/item-content/), [Item Title](/components/item-title/),
 * [Item Description](/components/item-description/) and [Item Actions](/components/item-actions/)
 * to build rich list rows. Stack several together with [Item Group](/components/item-group/).
 *
 * To make a whole Item act as a link, wrap it in an `<a>` element. The row
 * highlights on hover when wrapped this way.
 *
 * @status draft
 * @category list
 * @lightDom true
 *
 * @slot - Default slot for the item parts (media, content, actions, header, footer).
 *
 * @cssprop [--n-item-gap] - Controls the spacing between the item parts, using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-item-padding] - Controls the inner padding of the item, using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-item-border-radius=var(--n-border-radius-s)] - Controls how rounded the corners are.
 */
@customElement('nord-item')
export default class Item extends DraftComponentMixin(LitElement) {
  /**
   * The visual style of the item.
   *
   * - `default` has a transparent background and no border.
   * - `outline` adds a visible border.
   * - `muted` uses a subtle filled background for secondary content.
   */
  @property({ reflect: true }) variant: 'default' | 'outline' | 'muted' = 'default'

  /**
   * The size of the item, controlling its padding and gap.
   */
  @property({ reflect: true }) size: 'xs' | 's' | 'm' = 's'

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
  }

  // Render in the light DOM so the composed parts are the element's own content
  // and are styled by the injected stylesheet (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-item': Item
  }
}
